import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import ReactCrop from "react-image-crop";
import Tesseract from "tesseract.js";
import "react-image-crop/dist/ReactCrop.css";
import "./FolderData.css";
import PrintFormPage from "./PrintFormPage";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const PDF_WIDTH = 595;
const PDF_HEIGHT = 842;

// ─── Constants ────────────────────────────────────────────────────────────────
const POST_OPTIONS = [
  { hindi: "स्टेनो टाइपिस्ट", english: "Steno Typist" },
  { hindi: "आशुलिपिक", english: "Stenographer" },
  { hindi: "सूचीकार (केटलॉग)", english: "Cataloguer" },
  { hindi: "ओवरसियर", english: "Overseer" },
  { hindi: "प्रयोगशाला सहायक", english: "Laboratory Assistant" },
  { hindi: "लिपिक / कनिष्ठ सहायक", english: "Clerk / Junior Assistant" },
];

const VIBHAG_OPTIONS = [
  { hindi: "मनोविज्ञान विभाग", english: "Psychology Department" },
  { hindi: "सैन्य अध्ययन विभाग", english: "Military Studies Department" },
  { hindi: "गणित विभाग", english: "Mathematics Department" },
  { hindi: "रसायन विज्ञान विभाग", english: "Chemistry Department" },
  { hindi: "भौतिक विज्ञान विभाग", english: "Physics Department" },
  { hindi: "जन्तु विज्ञान विभाग", english: "Zoology Department" },
  { hindi: "वनस्पति विज्ञान विभाग", english: "Botany Department" },
];

const PAYMENT_OPTIONS = ["Bank Draft", "NEFT", "Check", "UPI", "IMPS", "RTGS"];

// ─── OCR Canvas Helpers ───────────────────────────────────────────────────────
function preprocessCanvasForOCR(srcCanvas) {
  const out = document.createElement("canvas");
  out.width = srcCanvas.width * 2;
  out.height = srcCanvas.height * 2;
  const ctx = out.getContext("2d");
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(srcCanvas, 0, 0, out.width, out.height);
  const imageData = ctx.getImageData(0, 0, out.width, out.height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    const contrasted = Math.min(255, Math.max(0, 1.8 * (gray - 128) + 128));
    const final =
      contrasted < 140 ? contrasted * 0.6 : Math.min(255, contrasted * 1.1);
    data[i] = data[i + 1] = data[i + 2] = final;
  }
  ctx.putImageData(imageData, 0, 0);
  return out;
}

function cropPdfCanvas(pdfCanvasEl, crop, displayWidth, displayHeight) {
  const scaleX = pdfCanvasEl.width / displayWidth;
  const scaleY = pdfCanvasEl.height / displayHeight;
  const out = document.createElement("canvas");
  out.width = Math.round(crop.width * scaleX);
  out.height = Math.round(crop.height * scaleY);
  out
    .getContext("2d")
    .drawImage(
      pdfCanvasEl,
      Math.round(crop.x * scaleX),
      Math.round(crop.y * scaleY),
      out.width,
      out.height,
      0,
      0,
      out.width,
      out.height,
    );
  return out;
}

// ─── Data Helpers ─────────────────────────────────────────────────────────────
const safeParseJSON = (val) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  try {
    let parsed = val;
    while (typeof parsed === "string") parsed = JSON.parse(parsed);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const mapBackendToForm = (data) => ({
  post: data?.post || "",
  vibhag: data?.vibhag || "",
  nameEnglish: data?.nameEnglish || data?.name_english || "",
  nameHindi: data?.nameHindi || data?.name_hindi || "",
  fatherName: data?.fatherName || data?.father_name || "",
  dob: data?.dob || "",
  gender: data?.gender || "",
  category: data?.category || "",
  religion: data?.religion || "",
  caste: data?.caste || "",
  aadharNumber: data?.aadharNumber || data?.aadhar_number || "",
  mobile: data?.mobile || "",
  email: data?.email || "",
  address: data?.address || "",
  correspondenceAddress: data?.correspondenceAddress || data?.correspondence_address || "",
  pinCode: data?.pinCode || data?.pin_code || "",
  hindiTyping: data?.hindiTyping || data?.hindi_typing || "",
  englishTyping: data?.englishTyping || data?.english_typing || "",
  shorthandSpeed: data?.shorthandSpeed || data?.shorthand_speed || "",
  paymentMethod: data?.paymentMethod || data?.payment_method || "",
  paymentNumber: data?.paymentNumber || data?.payment_number || "",
  sameAddress: false,
});

const mapEduRows = (raw) => {
  const rows = safeParseJSON(raw);
  if (!rows.length)
    return [
      {
        id: Date.now(),
        exam: "",
        customExam: "",
        board: "",
        isBoardChecked: false,
        totalMarks: "",
        obtainedMarks: "",
        percentage: "",
        scoreType: "Percentage",
      },
    ];
  return rows.map((row, i) => ({
    id: Date.now() + i,
    exam: row.l || row.exam || row.label || "",
    customExam: "",
    board: row.board || "",
    isBoardChecked: row.isBoardChecked || false,
    totalMarks: row.total || row.totalMarks || "",
    obtainedMarks: row.obtain || row.obtainedMarks || "",
    percentage: row.percentage || "",
    scoreType: row.scoreType || "Percentage",
  }));
};

const mapWorkRows = (raw) => {
  const rows = safeParseJSON(raw);
  if (!rows.length)
    return [{ id: 1, orgName: "", duration: "", post: "", salary: "" }];
  return rows.map((row, i) => ({
    id: i + 1,
    orgName: row.o || row.orgName || row.organization || "",
    post: row.p || row.post || "",
    duration: row.d || row.duration || "",
    salary: row.s || row.salary || "",
  }));
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export const FolderData = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get navigation state from location
  const { clickedFolder, allRows } = location.state || {};
  
  // ── Navigation State (NO API Calls - All data comes from navigation state) ──
  const [allCandidates, setAllCandidates] = useState(allRows || []);
  // const [currentIndex, setCurrentIndex] = useState(() => {
  //   if (initialIndex !== undefined && initialIndex >= 0 && allRows && initialIndex < allRows.length) {
  //     return initialIndex;
  //   }
  //   return 0;
  // });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentData, setCurrentData] = useState(null); // Don't initialize with selectedRow
  
  // CRITICAL: Always derive currentData from allCandidates[currentIndex]
  useEffect(() => {
    if (allRows && allRows.length > 0) {
      setAllCandidates(allRows);
      if (clickedFolder) {
        const foundIndex = allRows.findIndex(
          (item) => String(item.folder) === String(clickedFolder)
        );
        setCurrentIndex(foundIndex !== -1 ? foundIndex : 0);
      }
    }
  }, []); // runs once on mount only

  useEffect(() => {
    if (allCandidates.length > 0 && currentIndex >= 0 && currentIndex < allCandidates.length) {
      setCurrentData(allCandidates[currentIndex]);
    }
  }, [currentIndex, allCandidates]);
  
  // Navigation handlers
  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };
  
  const goToNext = () => {
    if (currentIndex < allCandidates.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };
  
  const handleDropdownChange = (e) => {
    const index = parseInt(e.target.value, 10);
    if (!isNaN(index) && index !== currentIndex) {
      setCurrentIndex(index);
    }
  };
  
  // ── Authentication Check ──
  useEffect(() => {
    const login = sessionStorage.getItem("ftoken");
    if (!login) {
      alert("Login First");
      navigate("/FolderLogin");
    }
  }, [navigate]);
  
  // Handle refresh case - no data in state
  useEffect(() => {
    if (!location.state || !allRows || allRows.length === 0) {
      console.warn("No navigation data found");
    }
  }, [location.state, allRows]);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const formRef = useRef({});
  const eduRowsRef = useRef([]);
  const workRowsRef = useRef([]);
  const folderRef = useRef("");
  const selectedFieldRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const pdfWrapperRef = useRef(null);
  const ocrLoadingRef = useRef(false);

  // ── PDF Viewer State ───────────────────────────────────────────────────────
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfScale, setPdfScale] = useState(1);
  const [pdfPageSize, setPdfPageSize] = useState({ width: 0, height: 0 });

  // ── OCR / Crop State ───────────────────────────────────────────────────────
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrMode, setOcrMode] = useState("idle");
  const [ocrLoading, setOcrLoading] = useState(false);
  const [selectedField, setSelectedField] = useState(null);

  // ── Photo / Signature State ────────────────────────────────────────────────
  const [photoSignUploading, setPhotoSignUploading] = useState(false);
  const [cropPreview, setCropPreview] = useState(null);
  const [savedPhoto, setSavedPhoto] = useState(null);
  const [savedSignature, setSavedSignature] = useState(null);

  // ── Form State ─────────────────────────────────────────────────────────────
  const [form, setForm] = useState(mapBackendToForm(null));
  const [eduRows, setEduRows] = useState([]);
  const [workRows, setWorkRows] = useState([]);
  const [validationErrors, setValidationErrors] = useState({
    mobile: "",
    email: "",
    aadhar: "",
    pinCode: "",
  });
  const [saveStatus, setSaveStatus] = useState("idle");
  const [saveMessage, setSaveMessage] = useState("");

  // CRITICAL: Update all form data when currentData changes
  useEffect(() => {
    if (!currentData) return;
    
    // Update form data
    const newForm = mapBackendToForm(currentData);
    setForm(newForm);
    formRef.current = newForm;
    
    // Update education rows
    const newEduRows = mapEduRows(currentData?.edu_rows);
    setEduRows(newEduRows);
    eduRowsRef.current = newEduRows;
    
    // Update work rows
    const newWorkRows = mapWorkRows(currentData?.work_rows);
    setWorkRows(newWorkRows);
    workRowsRef.current = newWorkRows;
    
    // Update folder reference
    folderRef.current = currentData?.folder || "";
    
    // Update photo/signature URLs
    setSavedPhoto(
      currentData?.photo_path
        ? `${process.env.REACT_APP_FILE_URL}/${currentData.photo_path}`
        : null,
    );
    setSavedSignature(
      currentData?.signature_path
        ? `${process.env.REACT_APP_FILE_URL}/${currentData.signature_path}`
        : null,
    );
    
    // Reset PDF page number when changing candidates
    setPageNumber(1);
    setNumPages(null);
    
    // Reset validation errors
    setValidationErrors({
      mobile: "",
      email: "",
      aadhar: "",
      pinCode: "",
    });
    
    // Reset OCR/crop states
    setCrop(null);
    setCompletedCrop(null);
    setOcrMode("idle");
    setCropPreview(null);
    setPhotoSignUploading(false);
    setSelectedField(null);
    selectedFieldRef.current = null;
    
  }, [currentData]);

  // ── Sync refs ─────────────────────────────────────────────────────────────
  useEffect(() => {
    formRef.current = form;
  }, [form]);
  useEffect(() => {
    eduRowsRef.current = eduRows;
  }, [eduRows]);
  useEffect(() => {
    workRowsRef.current = workRows;
  }, [workRows]);
  useEffect(() => {
    selectedFieldRef.current = selectedField;
    ocrLoadingRef.current = ocrLoading;
  }, [selectedField, ocrLoading]);

  // ── PDF helpers ────────────────────────────────────────────────────────────
  const calcFit = () => {
    if (!pdfContainerRef.current) return;
    const w = pdfContainerRef.current.offsetWidth - 48;
    const h = pdfContainerRef.current.offsetHeight - 32;
    setPdfScale(Math.min(w / PDF_WIDTH, h / PDF_HEIGHT));
  };
  const fitWidth = () => {
    if (pdfContainerRef.current)
      setPdfScale((pdfContainerRef.current.offsetWidth - 48) / PDF_WIDTH);
  };
  const fitHeight = () => {
    if (pdfContainerRef.current)
      setPdfScale((pdfContainerRef.current.offsetHeight - 32) / PDF_HEIGHT);
  };
  const zoomIn = () => setPdfScale((s) => Math.min(+(s + 0.15).toFixed(2), 4));
  const zoomOut = () =>
    setPdfScale((s) => Math.max(+(s - 0.15).toFixed(2), 0.2));

  const onPdfLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const onPdfPageRenderSuccess = () => {
    if (!pdfWrapperRef.current) return;
    const canvas = pdfWrapperRef.current.querySelector("canvas");
    if (canvas)
      setPdfPageSize({
        width: canvas.offsetWidth,
        height: canvas.offsetHeight,
      });
  };

  useEffect(() => {
    calcFit();
    window.addEventListener("resize", calcFit);
    return () => window.removeEventListener("resize", calcFit);
  }, []);

  const handleCropChange = (c) => {
    setCrop(c);
    if (ocrMode === "idle" || ocrMode === "done" || ocrMode === "error")
      setOcrMode("selecting");
  };

  // ── OCR ───────────────────────────────────────────────────────────────────
  const runOCR = useCallback(async () => {
    if (!selectedFieldRef.current) {
      alert("पहले कोई field पर click करें");
      return;
    }
    if (!completedCrop || !completedCrop.width || !completedCrop.height) {
      alert("PDF पर कोई क्षेत्र चुनें (खींचकर select करें)");
      return;
    }
    const pdfCanvas = pdfWrapperRef.current?.querySelector("canvas");
    if (!pdfCanvas) {
      alert("PDF canvas नहीं मिला। PDF लोड होने तक प्रतीक्षा करें।");
      return;
    }

    const cropped = cropPdfCanvas(
      pdfCanvas,
      completedCrop,
      pdfPageSize.width,
      pdfPageSize.height,
    );
    const processedCanvas = preprocessCanvasForOCR(cropped);

    setOcrLoading(true);
    ocrLoadingRef.current = true;
    setOcrMode("processing");
    setOcrProgress(0);

    try {
      const result = await Tesseract.recognize(processedCanvas, "eng+hin", {
        logger: (m) => {
          if (m.status === "recognizing text")
            setOcrProgress(Math.round(m.progress * 100));
        },
        tessedit_pageseg_mode: "6",
      });
      const text = result.data.text.trim();
      const field = selectedFieldRef.current;
      const updated = { ...formRef.current, [field]: text };
      setForm(updated);
      formRef.current = updated;
      setOcrMode("done");
      setCrop(null);
      setCompletedCrop(null);
    } catch (err) {
      console.error("Tesseract error:", err);
      setOcrMode("error");
    } finally {
      setOcrLoading(false);
      ocrLoadingRef.current = false;
    }
  }, [completedCrop, pdfPageSize]);

  // ── Photo / Signature upload ───────────────────────────────────────────────
  const uploadCroppedImage = async (type) => {
    if (!completedCrop || !completedCrop.width || !completedCrop.height) {
      alert("पहले PDF पर कोई क्षेत्र खींचें (drag करके select करें)");
      return;
    }
    const folder = folderRef.current;
    if (!folder) {
      alert("Folder not found");
      return;
    }
    const pdfCanvas = pdfWrapperRef.current?.querySelector("canvas");
    if (!pdfCanvas) {
      alert("PDF canvas नहीं मिला।");
      return;
    }

    const cropped = cropPdfCanvas(
      pdfCanvas,
      completedCrop,
      pdfPageSize.width,
      pdfPageSize.height,
    );
    const base64 = cropped.toDataURL("image/jpeg", 0.95);
    setCropPreview({ base64, type });
    setPhotoSignUploading(type);

    try {
      const payload = {
        folderName: folder,
        photo: type === "photo" ? base64 : null,
        signature: type === "signature" ? base64 : null,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_ADMIN_API_URL}/upload-photo-signature`,
        payload,
      );
      if (res.data.success) {
        if (type === "photo") setSavedPhoto(res.data.photoUrl || base64);
        if (type === "signature")
          setSavedSignature(res.data.signatureUrl || base64);
        alert(
          `✅ ${type === "photo" ? "फोटो" : "हस्ताक्षर"} सफलतापूर्वक सहेजा गया!`,
        );
        setCrop(null);
        setCompletedCrop(null);
        setOcrMode("idle");
        setCropPreview(null);
      } else {
        alert("❌ Upload failed");
        setCropPreview(null);
      }
    } catch (err) {
      console.error(err);
      alert(
        "❌ Upload failed: " + (err.response?.data?.message || err.message),
      );
      setCropPreview(null);
    } finally {
      setPhotoSignUploading(false);
    }
  };

  // fieldClass — highlights the form input targeted by OCR
  const fieldClass = (name) => {
    let c = "agc-field-input";
    if (selectedFieldRef.current === name) c += " agc-active";
    if (ocrLoadingRef.current && selectedFieldRef.current === name)
      c += " agc-loading-field";
    return c;
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateMobile = (v) =>
    !v
      ? ""
      : /^\d{10}$/.test(v)
        ? ""
        : "कृपया 10 अंकों का मोबाइल नंबर दर्ज करें";
  const validateEmail = (v) =>
    !v
      ? ""
      : /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v)
        ? ""
        : "कृपया वैध ईमेल आईडी दर्ज करें (जैसे: name@example.com)";
  const validateAadhar = (v) =>
    !v ? "" : /^\d{12}$/.test(v) ? "" : "कृपया 12 अंकों का आधार नंबर दर्ज करें";
  const validatePinCode = (v) =>
    !v ? "" : /^\d{6}$/.test(v) ? "" : "कृपया 6 अंकों का पिन कोड दर्ज करें";

  const handleMobileChange = (value) => {
    const digits = value.replace(/\D/g, "");
    setValidationErrors((p) => ({ ...p, mobile: validateMobile(digits) }));
    const u = { ...formRef.current, mobile: digits };
    setForm(u);
    formRef.current = u;
  };
  const handleEmailChange = (value) => {
    setValidationErrors((p) => ({ ...p, email: validateEmail(value) }));
    const u = { ...formRef.current, email: value };
    setForm(u);
    formRef.current = u;
  };
  const handleAadharChange = (value) => {
    const digits = value.replace(/\D/g, "");
    setValidationErrors((p) => ({ ...p, aadhar: validateAadhar(digits) }));
    const u = { ...formRef.current, aadharNumber: digits };
    setForm(u);
    formRef.current = u;
  };
  const handlePinCodeChange = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 6);
    setValidationErrors((p) => ({ ...p, pinCode: validatePinCode(digits) }));
    const u = { ...formRef.current, pinCode: digits };
    setForm(u);
    formRef.current = u;
  };

  // ── Education helpers ─────────────────────────────────────────────────────
  const calculatePercentage = (obtained, total) => {
    const o = parseFloat(obtained),
      t = parseFloat(total);
    if (!Number.isFinite(o) || !Number.isFinite(t) || t <= 0) return "";
    return ((o / t) * 100).toFixed(2);
  };

  const updateEduRow = (id, field, value) => {
    const rows = eduRowsRef.current.map((row) => {
      if (row.id !== id) return row;
      let r = { ...row, [field]: value };
      if (field === "scoreType") r.percentage = "";
      if (field === "totalMarks" || field === "obtainedMarks") {
        const st = r.scoreType || "Percentage";
        if (st === "Percentage")
          r.percentage = calculatePercentage(
            field === "obtainedMarks" ? value : r.obtainedMarks,
            field === "totalMarks" ? value : r.totalMarks,
          );
        else r.percentage = field === "obtainedMarks" ? value : r.obtainedMarks;
      }
      return r;
    });
    setEduRows(rows);
    eduRowsRef.current = rows;
  };

  const addEduRow = () => {
    const rows = [
      ...eduRowsRef.current,
      {
        id: Date.now(),
        exam: "",
        customExam: "",
        board: "",
        isBoardChecked: false,
        totalMarks: "",
        obtainedMarks: "",
        percentage: "",
        scoreType: "Percentage",
      },
    ];
    setEduRows(rows);
    eduRowsRef.current = rows;
  };

  const deleteEduRow = (id) => {
    const rows = eduRowsRef.current.filter((r) => r.id !== id);
    setEduRows(rows);
    eduRowsRef.current = rows;
  };

  // ── Work helpers ──────────────────────────────────────────────────────────
  const updateWorkRow = (id, field, value) => {
    const rows = workRowsRef.current.map((r) =>
      r.id === id ? { ...r, [field]: value } : r,
    );
    setWorkRows(rows);
    workRowsRef.current = rows;
  };

  const addWorkRow = () => {
    const rows = [
      ...workRowsRef.current,
      {
        id: workRowsRef.current.length + 1,
        orgName: "",
        duration: "",
        post: "",
        salary: "",
      },
    ];
    setWorkRows(rows);
    workRowsRef.current = rows;
  };

  // ── Update API (Only UPDATE allowed, no GET for data fetching) ──
  const handleUpdate = async () => {
    const mErr = validateMobile(formRef.current.mobile);
    const eErr = validateEmail(formRef.current.email);
    const aErr = validateAadhar(formRef.current.aadharNumber);
    const pErr = validatePinCode(formRef.current.pinCode);
    setValidationErrors({
      mobile: mErr,
      email: eErr,
      aadhar: aErr,
      pinCode: pErr,
    });
    if (mErr || eErr || aErr || pErr) {
      setSaveStatus("error");
      setSaveMessage("❌ कृपया सभी validation errors ठीक करें।");
      return;
    }
    const folder = folderRef.current;
    if (!folder) {
      setSaveStatus("error");
      setSaveMessage("❌ Folder नहीं मिला।");
      return;
    }
    setSaveStatus("saving");
    setSaveMessage("");
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/update-candidate/${folder}`,
        {
          form: formRef.current,
          eduRows: eduRowsRef.current,
          workRows: workRowsRef.current,
        },
      );
      if (res.data.success) {
        setSaveStatus("success");
        setSaveMessage("✅ Candidate सफलतापूर्वक अपडेट किया गया!");
        
        // Update local state to reflect changes
        const updatedCandidates = [...allCandidates];
        updatedCandidates[currentIndex] = {
          ...updatedCandidates[currentIndex],
          ...formRef.current,
          edu_rows: eduRowsRef.current,
          work_rows: workRowsRef.current,
        };
        setAllCandidates(updatedCandidates);
        setCurrentData(updatedCandidates[currentIndex]);
        
        setTimeout(() => setSaveStatus("idle"), 4000);
      } else {
        setSaveStatus("error");
        setSaveMessage(
          "❌ Update failed: " + (res.data.message || "Unknown error"),
        );
      }
    } catch (err) {
      console.error(err);
      setSaveStatus("error");
      setSaveMessage(
        "❌ Error: " + (err.response?.data?.message || err.message),
      );
    }
  };

  // ── Print ─────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    window.print();
  };

  // Navigation UI Component
  const NavigationControls = () => (
    <div className="agc-navigation-bar" style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "16px",
      padding: "12px 20px",
      background: "rgba(255,255,255,0.03)",
      borderRadius: "12px",
      marginBottom: "20px",
      flexWrap: "wrap",
    }}>
      <button
        className="agc-nav-btn"
        onClick={goToPrevious}
        disabled={currentIndex === 0}
        style={{
          padding: "8px 20px",
          borderRadius: "8px",
          border: "1px solid rgba(255,255,255,0.1)",
          background: currentIndex === 0 
            ? "rgba(255,255,255,0.05)" 
            : "linear-gradient(135deg, #1d4ed8, #1e40af)",
          color: "#fff",
          cursor: currentIndex === 0 ? "not-allowed" : "pointer",
          opacity: currentIndex === 0 ? 0.5 : 1,
          fontWeight: 600,
          fontSize: "14px",
          transition: "all 0.2s ease",
        }}
      >
        ◀ पिछला
      </button>

      <div className="agc-nav-center" style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        flex: 1,
        justifyContent: "center",
      }}>
        <select
          value={currentIndex}
          onChange={handleDropdownChange}
          style={{
            padding: "8px 16px",
            borderRadius: "8px",
            border: "1px solid rgba(255,255,255,0.15)",
            background: "rgba(0,0,0,0.1)",
            color: "black",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            minWidth: "220px",
            outline: "none",
          }}
        >
          {allCandidates.map((candidate, idx) => (
            <option key={idx} value={idx}>
              {idx + 1}. {candidate.folder || `Candidate ${idx + 1}`}
            </option>
          ))}
        </select>
        
        <span style={{
          padding: "6px 12px",
          background: "rgba(96, 136, 245, 0.2)",
          borderRadius: "20px",
          fontSize: "14px",
          fontWeight: 600,
          color: "#0e7fff",
        }}>
          {currentIndex + 1} / {allCandidates.length}
        </span>
      </div>

      <button
        className="agc-nav-btn"
        onClick={goToNext}
        disabled={currentIndex === allCandidates.length - 1}
        style={{
          padding: "8px 20px",
          borderRadius: "8px",
          border: "1px solid rgba(255,255,255,0.1)",
          background: currentIndex === allCandidates.length - 1 
            ? "rgba(255,255,255,0.05)" 
            : "linear-gradient(135deg, #1d4ed8, #1e40af)",
          color: "#fff",
          cursor: currentIndex === allCandidates.length - 1 ? "not-allowed" : "pointer",
          opacity: currentIndex === allCandidates.length - 1 ? 0.5 : 1,
          fontWeight: 600,
          fontSize: "14px",
          transition: "all 0.2s ease",
        }}
      >
        अगला ▶
      </button>
    </div>
  );

  // ── No data guard ─────────────────────────────────────────────────────────
  if (!location.state || !allCandidates.length || !currentData) {
    return (
      <div className="agc-container">
        <div className="agc-header">
          <div className="agc-header-icon">📋</div>
          <div>
            <h1 className="agc-title">Folder Data</h1>
            <p className="agc-subtitle">कोई डेटा नहीं मिला</p>
          </div>
          <button className="agc-nav-btn" onClick={() => navigate(-1)}>
            ← वापस जाएं
          </button>
        </div>
        <div
          style={{
            textAlign: "center",
            padding: "60px 24px",
            color: "var(--agc-text-2)",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
          <h2 style={{ marginBottom: 8 }}>कोई डेटा नहीं मिला</h2>
          <p style={{ marginBottom: 24 }}>
            कृपया पिछले पेज से सही डेटा के साथ आएं या रिफ्रेश करें।
          </p>
          <button
            className="agc-nav-btn"
            style={{ marginTop: 24 }}
            onClick={() => navigate("/your-table-page")}
          >
            ← टेबल पर जाएं
          </button>
        </div>
      </div>
    );
  }

  const folder = currentData?.folder;
  const pdfUrl = `${process.env.REACT_APP_FILE_URL}/uploads/${currentData?.folder}.pdf`;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <PrintFormPage
        form={form}
        folder={folder}
        eduRows={eduRows}
        workRows={workRows}
        savedPhoto={savedPhoto}
        savedSignature={savedSignature}
      />

      <div className="agc-container agc-no-print">
        {/* ── Header ── */}
        <div className="agc-header">
          <div className="agc-header-icon">📋</div>
          <div>
            <h1 className="agc-title">Candidate विवरण</h1>
            <p className="agc-subtitle">
              Folder: <strong>{currentData?.folder}</strong>
            </p>
          </div>
          <button className="agc-nav-btn" onClick={() => navigate(-1)}>
            ← वापस जाएं
          </button>
        </div>

        {/* Navigation Controls */}
        <NavigationControls />

        {/* ── Status Banners ── */}
        {saveStatus === "success" && (
          <div
            style={{
              padding: "12px 20px",
              borderRadius: 10,
              marginBottom: 12,
              fontWeight: 600,
              fontSize: 14,
              background: "rgba(34,197,94,0.15)",
              border: "1px solid rgba(34,197,94,0.4)",
              color: "#86efac",
            }}
          >
            {saveMessage}
          </div>
        )}
        {saveStatus === "error" && (
          <div
            style={{
              padding: "12px 20px",
              borderRadius: 10,
              marginBottom: 12,
              fontWeight: 600,
              fontSize: 14,
              background: "rgba(239,68,68,0.15)",
              border: "1px solid rgba(239,68,68,0.4)",
              color: "#fca5a5",
            }}
          >
            {saveMessage}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            TWO-PANEL LAYOUT
        ══════════════════════════════════════════════════════════════════ */}
        <div className="agc-main">
          {/* LEFT: PDF PANEL */}
          <div className="agc-image-panel">
            <div className="agc-panel-header">
              <span className="agc-panel-icon">📄</span>
              <h2>PDF दस्तावेज़</h2>
            </div>

            {/* Sticky Toolbar */}
            <div
              className="agc-toolbar"
              style={{
                position: "sticky",
                top: 0,
                zIndex: 100,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                flexWrap: "wrap",
                gap: 6,
              }}
            >
              <div className="agc-toolbar-group">
                <button
                  className="agc-toolbar-btn"
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber((p) => p - 1)}
                >
                  <span className="agc-btn-icon">&#9664;</span>
                  <span className="agc-btn-label">Prev</span>
                </button>
                <span className="agc-page-info">
                  <span className="agc-page-current">{pageNumber}</span>
                  <span className="agc-page-sep">/</span>
                  <span className="agc-page-total">{numPages ?? "—"}</span>
                </span>
                <button
                  className="agc-toolbar-btn"
                  disabled={!numPages || pageNumber >= numPages}
                  onClick={() => setPageNumber((p) => p + 1)}
                >
                  <span className="agc-btn-label">Next</span>
                  <span className="agc-btn-icon">&#9654;</span>
                </button>
              </div>

              <div className="agc-toolbar-divider" />

              <div className="agc-toolbar-group">
                <button
                  className="agc-toolbar-btn agc-icon-only"
                  onClick={zoomOut}
                >
                  &#8722;
                </button>
                <span className="agc-zoom-label">
                  {Math.round(pdfScale * 100)}%
                </span>
                <button
                  className="agc-toolbar-btn agc-icon-only"
                  onClick={zoomIn}
                >
                  &#43;
                </button>
              </div>

              <div className="agc-toolbar-divider" />

              <div className="agc-toolbar-group">
                <button className="agc-toolbar-btn" onClick={fitWidth}>
                  &#8596; Width
                </button>
                <button className="agc-toolbar-btn" onClick={fitHeight}>
                  &#8597; Height
                </button>
                <button className="agc-toolbar-btn" onClick={calcFit}>
                  &#9974; Fit
                </button>
              </div>

              <div className="agc-toolbar-divider" />

              <div className="agc-toolbar-group" style={{ marginLeft: "auto" }}>
                <button
                  onClick={runOCR}
                  disabled={ocrMode === "processing"}
                  title="PDF से Scan करें (पहले field चुनें, फिर PDF पर खींचें)"
                  style={{
                    padding: "6px 14px",
                    borderRadius: 6,
                    border: "none",
                    cursor:
                      ocrMode === "processing" ? "not-allowed" : "pointer",
                    fontWeight: 700,
                    fontSize: 12,
                    color: "#fff",
                    background:
                      ocrMode === "processing"
                        ? "#374151"
                        : "linear-gradient(135deg, #1d4ed8, #1e40af)",
                    boxShadow:
                      ocrMode === "processing"
                        ? "none"
                        : "0 2px 6px rgba(29,78,216,0.45)",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    whiteSpace: "nowrap",
                    transition: "all 0.2s ease",
                    opacity: ocrMode === "processing" ? 0.7 : 1,
                  }}
                >
                  {ocrMode === "processing" ? (
                    <>
                      <span
                        style={{
                          display: "inline-block",
                          width: 11,
                          height: 11,
                          border: "2px solid rgba(255,255,255,0.4)",
                          borderTopColor: "#fff",
                          borderRadius: "50%",
                          animation: "agc-ocr-spin 0.7s linear infinite",
                          flexShrink: 0,
                        }}
                      />
                      {ocrProgress}%
                    </>
                  ) : (
                    <>🔍 Scan करें</>
                  )}
                </button>
              </div>
            </div>

            {/* OCR status bar */}
            <div
              className="agc-ocr-status-bar"
              style={{ margin: "0", borderRadius: 0 }}
            >
              {ocrMode === "idle" && !selectedField && (
                <span className="agc-status-hint">
                  👆 पहले कोई field पर click करें
                </span>
              )}
              {ocrMode === "idle" && selectedField && (
                <span className="agc-status-hint agc-active">
                  ✏️ फ़ील्ड चुना: <strong>{selectedField}</strong> — अब PDF पर
                  क्षेत्र खींचें
                </span>
              )}
              {ocrMode === "selecting" && (
                <span className="agc-status-hint agc-active">
                  ✂️ क्षेत्र चुना जा रहा है — "Scan करें" दबाएं
                </span>
              )}
              {ocrMode === "processing" && (
                <span className="agc-status-loading">
                  ⏳ OCR चल रहा है ({ocrProgress}%) —{" "}
                  <strong>{selectedField}</strong>
                </span>
              )}
              {ocrMode === "done" && (
                <span className="agc-status-done">
                  ✅ Text भर दिया गया: <strong>{selectedField}</strong>
                </span>
              )}
              {ocrMode === "error" && (
                <span className="agc-status-error">
                  ❌ OCR में त्रुटि हुई, दोबारा कोशिश करें
                </span>
              )}
            </div>

            {/* PDF Area with ReactCrop overlay */}
            <div className="agc-pdf-area" ref={pdfContainerRef}>
              {pdfUrl ? (
                <div
                  style={{ position: "relative", display: "inline-block" }}
                  ref={pdfWrapperRef}
                >
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onPdfLoadSuccess}
                    loading={
                      <div className="agc-pdf-loading">
                        <div className="agc-spinner"></div>
                        <p>PDF लोड हो रहा है…</p>
                      </div>
                    }
                    error={
                      <div className="agc-pdf-error">Failed to load PDF.</div>
                    }
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={pdfScale}
                      renderAnnotationLayer={false}
                      renderTextLayer={true}
                      onRenderSuccess={onPdfPageRenderSuccess}
                    />
                  </Document>

                  {pdfPageSize.width > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: pdfPageSize.width,
                        height: pdfPageSize.height,
                        zIndex: 10,
                      }}
                    >
                      <ReactCrop
                        crop={crop}
                        onChange={handleCropChange}
                        onComplete={(c) => setCompletedCrop(c)}
                        style={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <img
                          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                          alt=""
                          style={{
                            width: pdfPageSize.width,
                            height: pdfPageSize.height,
                            opacity: 0,
                            display: "block",
                            userSelect: "none",
                            pointerEvents: "none",
                          }}
                          draggable={false}
                        />
                      </ReactCrop>
                    </div>
                  )}

                  {ocrMode === "processing" && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: "#222",
                        zIndex: 20,
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${ocrProgress}%`,
                          background: "#57c8ff",
                          transition: "width .3s ease",
                        }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="agc-pdf-loading">
                  <span style={{ fontSize: 40 }}>📄</span>
                  <p>PDF उपलब्ध नहीं है</p>
                </div>
              )}
            </div>

            {/* OCR language note */}
            <div className="agc-ocr-section" style={{ padding: "8px 12px" }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#9ca3af",
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    padding: "2px 8px",
                    background: "rgba(29,78,216,0.15)",
                    border: "1px solid rgba(29,78,216,0.3)",
                    borderRadius: 4,
                    color: "#93c5fd",
                    fontWeight: 600,
                    fontSize: 10,
                    letterSpacing: "0.05em",
                  }}
                >
                  eng+hin
                </span>
                Hindi &amp; English OCR सक्रिय है
              </div>
            </div>
          </div>

          {/* RIGHT: FORM PANEL */}
          <div className="agc-form-panel">
            <div className="agc-panel-header">
              <span className="agc-panel-icon">📝</span>
              <h2>अभ्यर्थी विवरण</h2>
            </div>

            {/* Section 1 — Basic Info */}
            <div className="agc-form-section">
              <div className="agc-section-title">
                <span className="agc-section-num">1</span>मूल जानकारी
              </div>

              <div className="agc-field-group">
                <label>पद का नाम</label>
                <select
                  className={fieldClass("post")}
                  value={form.post}
                  onFocus={() => setSelectedField("post")}
                  onChange={(e) => {
                    const u = { ...formRef.current, post: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                >
                  <option value="">पद का नाम चुनें</option>
                  {POST_OPTIONS.map((p, i) => (
                    <option key={i} value={p.hindi}>
                      {p.hindi} ({p.english})
                    </option>
                  ))}
                </select>
              </div>

              <div className="agc-field-group">
                <label>विभाग</label>
                <select
                  className={fieldClass("vibhag")}
                  value={form.vibhag}
                  onFocus={() => setSelectedField("vibhag")}
                  onChange={(e) => {
                    const u = { ...formRef.current, vibhag: e.target.value };
                    setForm(u);
                    formRef.current = u;
                  }}
                >
                  <option value="">विभाग चुनें</option>
                  {VIBHAG_OPTIONS.map((v, i) => (
                    <option key={i} value={v.hindi}>
                      {v.hindi} ({v.english})
                    </option>
                  ))}
                </select>
              </div>

              <div className="agc-two-col">
                <div className="agc-field-group">
                  <label>नाम (English)</label>
                  <input
                    className={fieldClass("nameEnglish")}
                    placeholder="Full Name in English"
                    value={form.nameEnglish}
                    onFocus={() => setSelectedField("nameEnglish")}
                    onChange={(e) => {
                      const u = {
                        ...formRef.current,
                        nameEnglish: e.target.value,
                      };
                      setForm(u);
                      formRef.current = u;
                    }}
                  />
                </div>
                <div className="agc-field-group">
                  <label>नाम (हिंदी)</label>
                  <input
                    className={fieldClass("nameHindi")}
                    placeholder="पूरा नाम हिंदी में"
                    value={form.nameHindi}
                    onFocus={() => setSelectedField("nameHindi")}
                    onChange={(e) => {
                      const u = {
                        ...formRef.current,
                        nameHindi: e.target.value,
                      };
                      setForm(u);
                      formRef.current = u;
                    }}
                  />
                </div>
              </div>

              <div className="agc-two-col">
                <div className="agc-field-group">
                  <label>पिता / पति का नाम</label>
                  <input
                    className={fieldClass("fatherName")}
                    placeholder="पिता / पति का नाम"
                    value={form.fatherName}
                    onFocus={() => setSelectedField("fatherName")}
                    onChange={(e) => {
                      const u = {
                        ...formRef.current,
                        fatherName: e.target.value,
                      };
                      setForm(u);
                      formRef.current = u;
                    }}
                  />
                </div>
                <div className="agc-field-group">
                  <label>जन्म तिथि</label>
                  <input
                    type="date"
                    className={fieldClass("dob")}
                    value={form.dob}
                    onFocus={() => setSelectedField("dob")}
                    onChange={(e) => {
                      const u = { ...formRef.current, dob: e.target.value };
                      setForm(u);
                      formRef.current = u;
                    }}
                  />
                </div>
              </div>

              <div className="agc-field-group">
                <label>लिंग</label>
                <div className="agc-radio-group">
                  {[
                    ["male", "पुरुष"],
                    ["female", "महिला"],
                    ["other", "अन्य"],
                  ].map(([v, lbl]) => (
                    <label
                      key={v}
                      className={`agc-radio-option ${
                        form.gender === v ? "agc-radio-active" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="fd-gender"
                        value={v}
                        checked={form.gender === v}
                        onChange={(e) => {
                          const u = {
                            ...formRef.current,
                            gender: e.target.value,
                          };
                          setForm(u);
                          formRef.current = u;
                        }}
                      />
                      {lbl}
                    </label>
                  ))}
                </div>
              </div>

              <div className="agc-two-col">
                <div className="agc-field-group">
                  <label>श्रेणी</label>
                  <select
                    className={fieldClass("category")}
                    value={form.category}
                    onFocus={() => setSelectedField("category")}
                    onChange={(e) => {
                      const u = {
                        ...formRef.current,
                        category: e.target.value,
                      };
                      setForm(u);
                      formRef.current = u;
                    }}
                  >
                    <option value="">श्रेणी चुनें</option>
                    {["General", "EWS", "OBC", "SC", "ST"].map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div className="agc-field-group">
                  <label>धर्म</label>
                  <input
                    list="fd-religion-options"
                    className={fieldClass("religion")}
                    placeholder="धर्म चुनें या लिखें"
                    value={form.religion}
                    onFocus={() => setSelectedField("religion")}
                    onChange={(e) => {
                      const u = {
                        ...formRef.current,
                        religion: e.target.value,
                      };
                      setForm(u);
                      formRef.current = u;
                    }}
                  />
                  <datalist id="fd-religion-options">
                    {[
                      "Hindu",
                      "Muslim",
                      "Sikh",
                      "Christian",
                      "Jain",
                      "Buddhist",
                      "Other",
                    ].map((o) => (
                      <option key={o} value={o} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="agc-two-col">
                <div className="agc-field-group">
                  <label>जाति</label>
                  <input
                    className={fieldClass("caste")}
                    placeholder="जाति"
                    value={form.caste}
                    onFocus={() => setSelectedField("caste")}
                    onChange={(e) => {
                      const u = { ...formRef.current, caste: e.target.value };
                      setForm(u);
                      formRef.current = u;
                    }}
                  />
                </div>
                <div className="agc-field-group">
                  <label>मोबाइल नंबर</label>
                  <input
                    className={`${fieldClass("mobile")} ${
                      validationErrors.mobile ? "agc-error-field" : ""
                    }`}
                    placeholder="मोबाइल नंबर (10 अंक)"
                    value={form.mobile}
                    maxLength={10}
                    onFocus={() => setSelectedField("mobile")}
                    onChange={(e) => handleMobileChange(e.target.value)}
                  />
                  {validationErrors.mobile && (
                    <small
                      style={{
                        color: "#ef4444",
                        marginTop: 4,
                        display: "block",
                      }}
                    >
                      ⚠️ {validationErrors.mobile}
                    </small>
                  )}
                </div>
              </div>

              <div className="agc-field-group">
                <label>ई-मेल आईडी</label>
                <input
                  className={`${fieldClass("email")} ${
                    validationErrors.email ? "agc-error-field" : ""
                  }`}
                  placeholder="ई-मेल आईडी (जैसे: name@example.com)"
                  value={form.email}
                  onFocus={() => setSelectedField("email")}
                  onChange={(e) => handleEmailChange(e.target.value)}
                />
                {validationErrors.email && (
                  <small
                    style={{ color: "#ef4444", marginTop: 4, display: "block" }}
                  >
                    ⚠️ {validationErrors.email}
                  </small>
                )}
              </div>

              <div className="agc-field-group">
                <label>आधार संख्या</label>
                <input
                  className={`${fieldClass("aadharNumber")} ${
                    validationErrors.aadhar ? "agc-error-field" : ""
                  }`}
                  placeholder="आधार संख्या (12 अंक)"
                  value={form.aadharNumber}
                  maxLength={12}
                  onFocus={() => setSelectedField("aadharNumber")}
                  onChange={(e) => handleAadharChange(e.target.value)}
                />
                {validationErrors.aadhar && (
                  <small
                    style={{ color: "#ef4444", marginTop: 4, display: "block" }}
                  >
                    ⚠️ {validationErrors.aadhar}
                  </small>
                )}
              </div>

              <div className="agc-field-group">
                <label>वर्तमान पता</label>
                <textarea
                  className={`${fieldClass("address")} agc-field-textarea`}
                  placeholder="वर्तमान पता"
                  value={form.address}
                  onFocus={() => setSelectedField("address")}
                  onChange={(e) => {
                    const v = e.target.value;
                    const u = {
                      ...formRef.current,
                      address: v,
                      correspondenceAddress: formRef.current.sameAddress
                        ? v
                        : formRef.current.correspondenceAddress,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>

              <div className="agc-field-group">
                <label
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <input
                    type="checkbox"
                    checked={form.sameAddress || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const u = {
                        ...formRef.current,
                        sameAddress: checked,
                        correspondenceAddress: checked
                          ? formRef.current.address
                          : formRef.current.correspondenceAddress,
                      };
                      setForm(u);
                      formRef.current = u;
                    }}
                  />
                  वर्तमान पता ही पत्राचार पता है
                </label>
              </div>

              <div className="agc-field-group">
                <label>पत्राचार पता</label>
                <textarea
                  className={`${fieldClass(
                    "correspondenceAddress",
                  )} agc-field-textarea`}
                  placeholder="पत्राचार पता"
                  value={form.correspondenceAddress}
                  disabled={form.sameAddress}
                  onFocus={() => setSelectedField("correspondenceAddress")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      correspondenceAddress: e.target.value,
                      sameAddress: false,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>

              <div className="agc-field-group">
                <label>पिन कोड</label>
                <input
                  className={`${fieldClass("pinCode")} ${
                    validationErrors.pinCode ? "agc-error-field" : ""
                  }`}
                  placeholder="पिन कोड (6 अंक)"
                  value={form.pinCode || ""}
                  maxLength={6}
                  onFocus={() => setSelectedField("pinCode")}
                  onChange={(e) => handlePinCodeChange(e.target.value)}
                />
                {validationErrors.pinCode && (
                  <small
                    style={{ color: "#ef4444", marginTop: 4, display: "block" }}
                  >
                    ⚠️ {validationErrors.pinCode}
                  </small>
                )}
              </div>
            </div>

            {/* Section 2 — Educational Qualification */}
            <div className="agc-form-section">
              <div className="agc-section-title">
                <span className="agc-section-num">2</span>शैक्षिक योग्यता
              </div>
              <div className="agc-table-wrapper">
                <table className="agc-data-table">
                  <thead>
                    <tr>
                      <th>क्र.सं.</th>
                      <th>परीक्षा</th>
                      <th>बोर्ड</th>
                      <th>Score Type</th>
                      <th>पूर्णांक</th>
                      <th>प्राप्तांक</th>
                      <th>Result</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {eduRows.map((row, idx) => (
                      <tr key={row.id}>
                        <td>{idx + 1}</td>
                        <td>
                          <select
                            className="agc-table-input"
                            value={row.exam || ""}
                            onChange={(e) =>
                              updateEduRow(row.id, "exam", e.target.value)
                            }
                          >
                            <option value="">Select</option>
                            {[
                              "Highschool",
                              "Intermediate",
                              "Graduation",
                              "Post Graduation",
                              "CCC",
                              "O-Level",
                              "Other",
                            ].map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                          {row.exam === "Other" && (
                            <input
                              className="agc-table-input"
                              placeholder="Custom Exam"
                              value={row.customExam}
                              onChange={(e) =>
                                updateEduRow(
                                  row.id,
                                  "customExam",
                                  e.target.value,
                                )
                              }
                            />
                          )}
                        </td>
                        <td>
                          <input
                            className="agc-table-input"
                            value={row.board}
                            placeholder="Board"
                            onChange={(e) =>
                              updateEduRow(row.id, "board", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <select
                            className="agc-table-input"
                            value={row.scoreType}
                            onChange={(e) =>
                              updateEduRow(row.id, "scoreType", e.target.value)
                            }
                          >
                            <option value="Percentage">Percentage</option>
                            <option value="CGPA">CGPA</option>
                            <option value="Grade">Grade</option>
                          </select>
                        </td>
                        <td>
                          <input
                            className="agc-table-input agc-small"
                            value={row.totalMarks}
                            placeholder="Total"
                            onChange={(e) =>
                              updateEduRow(row.id, "totalMarks", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="agc-table-input agc-small"
                            value={row.obtainedMarks}
                            placeholder={
                              row.scoreType === "CGPA"
                                ? "CGPA"
                                : row.scoreType === "Grade"
                                  ? "Grade"
                                  : "Marks"
                            }
                            onChange={(e) =>
                              updateEduRow(
                                row.id,
                                "obtainedMarks",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="agc-table-input agc-small"
                            value={row.percentage}
                            readOnly
                            placeholder={
                              row.scoreType === "CGPA"
                                ? "CGPA"
                                : row.scoreType === "Grade"
                                  ? "Grade"
                                  : "%"
                            }
                          />
                        </td>
                        <td>
                          <button
                            onClick={() => deleteEduRow(row.id)}
                            className="agc-delete-row-btn"
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button className="agc-add-row-btn" onClick={addEduRow}>
                + Add Qualification
              </button>
            </div>

            {/* Section 3 — Typing & Shorthand Skills */}
            <div className="agc-form-section">
              <div className="agc-section-title">
                <span className="agc-section-num">3</span>टंकण एवं आशुलिपि की दक्षता
              </div>
              <div className="agc-two-col">
                <div className="agc-field-group">
                  <label>हिंदी टंकण गति</label>
                  <input
                    className={fieldClass("hindiTyping")}
                    placeholder="जैसे: 25 w/m"
                    value={form.hindiTyping}
                    onFocus={() => setSelectedField("hindiTyping")}
                    onChange={(e) => {
                      const u = {
                        ...formRef.current,
                        hindiTyping: e.target.value,
                      };
                      setForm(u);
                      formRef.current = u;
                    }}
                  />
                </div>
                <div className="agc-field-group">
                  <label>अंग्रेजी टंकण गति</label>
                  <input
                    className={fieldClass("englishTyping")}
                    placeholder="जैसे: 40 w/m"
                    value={form.englishTyping}
                    onFocus={() => setSelectedField("englishTyping")}
                    onChange={(e) => {
                      const u = {
                        ...formRef.current,
                        englishTyping: e.target.value,
                      };
                      setForm(u);
                      formRef.current = u;
                    }}
                  />
                </div>
              </div>
              <div className="agc-field-group">
                <label>आशुलिपि गति</label>
                <input
                  className={fieldClass("shorthandSpeed")}
                  placeholder="जैसे: 80 w/m"
                  value={form.shorthandSpeed}
                  onFocus={() => setSelectedField("shorthandSpeed")}
                  onChange={(e) => {
                    const u = {
                      ...formRef.current,
                      shorthandSpeed: e.target.value,
                    };
                    setForm(u);
                    formRef.current = u;
                  }}
                />
              </div>
            </div>

            {/* Section 4 — Work Experience */}
            <div className="agc-form-section">
              <div className="agc-section-title">
                <span className="agc-section-num">4</span>कार्य अनुभव का विवरण
              </div>
              <div className="agc-table-wrapper">
                <table className="agc-data-table">
                  <thead>
                    <tr>
                      <th>क्र.सं.</th>
                      <th>संस्था का नाम एवं पता</th>
                      <th>अवधि (कब से कब तक)</th>
                      <th>पद</th>
                      <th>वेतनमान</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workRows.map((row) => (
                      <tr key={row.id}>
                        <td className="agc-row-num">
                          {String(row.id).padStart(2, "0")}
                        </td>
                        <td>
                          <textarea
                            className="agc-table-input agc-table-textarea"
                            value={row.orgName}
                            placeholder="संस्था का नाम एवं पता"
                            onChange={(e) =>
                              updateWorkRow(row.id, "orgName", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="agc-table-input"
                            value={row.duration}
                            placeholder="01/2020 से 03/2023"
                            onChange={(e) =>
                              updateWorkRow(row.id, "duration", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="agc-table-input"
                            value={row.post}
                            placeholder="पद"
                            onChange={(e) =>
                              updateWorkRow(row.id, "post", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="agc-table-input"
                            value={row.salary}
                            placeholder="वेतन"
                            onChange={(e) =>
                              updateWorkRow(row.id, "salary", e.target.value)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button className="agc-add-row-btn" onClick={addWorkRow}>
                + पंक्ति जोड़ें
              </button>
            </div>

            {/* Section 5 — Payment Information */}
            <div className="agc-form-section">
              <div className="agc-section-title">
                <span className="agc-section-num">5</span>भुगतान जानकारी
              </div>
              <div className="agc-two-col">
                <div className="agc-field-group">
                  <label>भुगतान विधि</label>
                  <select
                    className={fieldClass("paymentMethod")}
                    value={form.paymentMethod}
                    onFocus={() => setSelectedField("paymentMethod")}
                    onChange={(e) => {
                      const u = {
                        ...formRef.current,
                        paymentMethod: e.target.value,
                        paymentNumber: "",
                      };
                      setForm(u);
                      formRef.current = u;
                    }}
                  >
                    <option value="">भुगतान विधि चुनें</option>
                    {PAYMENT_OPTIONS.map((m, i) => (
                      <option key={i} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="agc-field-group">
                  <label>
                    {form.paymentMethod === "Check"
                      ? "चेक नंबर"
                      : form.paymentMethod === "UPI"
                        ? "UPI आईडी"
                        : form.paymentMethod === "NEFT"
                          ? "NEFT लेन-देन संख्या"
                          : form.paymentMethod === "IMPS"
                            ? "IMPS लेन-देन संख्या"
                            : form.paymentMethod === "RTGS"
                              ? "RTGS लेन-देन संख्या"
                              : "भुगतान संख्या"}
                  </label>
                  <input
                    className={fieldClass("paymentNumber")}
                    type="text"
                    value={form.paymentNumber}
                    disabled={!form.paymentMethod}
                    onFocus={() => setSelectedField("paymentNumber")}
                    onChange={(e) => {
                      const u = {
                        ...formRef.current,
                        paymentNumber: e.target.value,
                      };
                      setForm(u);
                      formRef.current = u;
                    }}
                  />
                  {!form.paymentMethod && (
                    <small
                      style={{ color: "#666", marginTop: 4, display: "block" }}
                    >
                      पहले भुगतान विधि चुनें
                    </small>
                  )}
                </div>
              </div>
            </div>

            {/* Section 6 — Photo & Signature Preview */}
            <div className="agc-form-section">
              <div className="agc-photo-sign-preview-card">
                <div className="agc-preview-card-header">
                  <span className="agc-preview-card-icon agc-photo-icon">📸</span>
                  <span className="agc-preview-card-label">Photo Preview</span>
                </div>
                <div className="agc-preview-img-box">
                  {savedPhoto ? (
                    <img
                      src={savedPhoto}
                      alt="Candidate Photo"
                      className="agc-preview-img"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className="agc-preview-placeholder"
                    style={{ display: savedPhoto ? "none" : "flex" }}
                  >
                    <span className="agc-placeholder-icon">👤</span>
                    <span className="agc-placeholder-text">Photo Not Available</span>
                    <span className="agc-placeholder-sub">PDF से खींचकर Photo save करें</span>
                  </div>
                </div>
                {savedPhoto && (
                  <div className="agc-preview-badge agc-photo-badge">✅ Photo Saved</div>
                )}
              </div>

              <div className="agc-photo-sign-preview-card agc-sign-card">
                <div className="agc-preview-card-header">
                  <span className="agc-preview-card-icon agc-sign-icon">✍️</span>
                  <span className="agc-preview-card-label">Signature Preview</span>
                </div>
                <div className="agc-preview-img-box agc-sign-img-box">
                  {savedSignature ? (
                    <img
                      src={savedSignature}
                      alt="Candidate Signature"
                      className="agc-preview-img agc-signature-img"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className="agc-preview-placeholder"
                    style={{ display: savedSignature ? "none" : "flex" }}
                  >
                    <span className="agc-placeholder-icon">✒️</span>
                    <span className="agc-placeholder-text">Signature Not Available</span>
                    <span className="agc-placeholder-sub">PDF से खींचकर Signature save करें</span>
                  </div>
                </div>
                {savedSignature && (
                  <div className="agc-preview-badge agc-sign-badge">✅ Signature Saved</div>
                )}
              </div>

              {/* <div className="agc-two-col" style={{ marginTop: 16 }}>
                <button
                  onClick={() => uploadCroppedImage("photo")}
                  disabled={!!photoSignUploading}
                  style={{
                    padding: "11px 16px",
                    borderRadius: 8,
                    border: "none",
                    cursor: photoSignUploading ? "not-allowed" : "pointer",
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#fff",
                    background: photoSignUploading === "photo"
                      ? "#9ca3af"
                      : "linear-gradient(135deg, #1d4ed8, #1e40af)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    width: "100%",
                  }}
                >
                  {photoSignUploading === "photo" ? (
                    <>⏳ सहेज रहा है…</>
                  ) : (
                    <>📸 Save Photo (PDF से)</>
                  )}
                </button>

                <button
                  onClick={() => uploadCroppedImage("signature")}
                  disabled={!!photoSignUploading}
                  style={{
                    padding: "11px 16px",
                    borderRadius: 8,
                    border: "none",
                    cursor: photoSignUploading ? "not-allowed" : "pointer",
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#fff",
                    background: photoSignUploading === "signature"
                      ? "#9ca3af"
                      : "linear-gradient(135deg, #7c3aed, #6d28d9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    width: "100%",
                  }}
                >
                  {photoSignUploading === "signature" ? (
                    <>⏳ सहेज रहा है…</>
                  ) : (
                    <>✍️ Save Signature (PDF से)</>
                  )}
                </button>
              </div> */}
            </div>

            {/* Action Buttons */}
            <div className="agc-fd-action-bar">
              <button className="agc-print-btn" onClick={handlePrint}>
                🖨️ Print Out
              </button>
              {/* <button className="agc-update-btn" onClick={handleUpdate}>
                💾 अपडेट करें
              </button> */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FolderData;